import { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Palette, Code2, AtSign, Server, Search, MessageCircle, ArrowUpRight } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import InquiryDialog from "@/components/InquiryDialog";

const FEATURES = [
  { icon: Palette, label: "Custom Website Design" },
  { icon: Code2, label: "Custom Development" },
  { icon: AtSign, label: "Domain Registration" },
  { icon: Server, label: "High-Speed Hosting" },
  { icon: Search, label: "SEO Kickoff" },
  { icon: MessageCircle, label: "WhatsApp Lead Integration" },
];

const AllInOne = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section id="all-in-one" ref={ref} className="relative overflow-hidden" data-testid="all-in-one-section">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12 lg:py-32">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <FadeUp>
            <p className="mono text-xs uppercase tracking-[0.2em] text-[#E9C766] sm:text-sm">
              The flagship package
            </p>
            <h2 className="font-display mt-4 text-xl font-bold tracking-tight text-white sm:text-2xl lg:text-3xl">
              One plug. <span className="text-[#D9A92E]">Full power.</span>
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-[#A1A1AA] sm:text-lg">
              The All-In-One Website Package bundles everything a business needs to dominate
              online — strategy, design, development, domain, hosting, SEO kickoff and WhatsApp
              lead capture — into a single turnkey launch with one team and one point of contact.
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {FEATURES.map((f) => (
                <div key={f.label} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#131316] px-4 py-3.5 transition-colors duration-300 hover:border-[#D9A92E]/40">
                  <f.icon className="h-4.5 w-4.5 shrink-0 text-[#D9A92E]" />
                  <span className="text-sm font-medium text-white">{f.label}</span>
                </div>
              ))}
            </div>
            <div className="mt-9 flex flex-wrap items-center gap-5">
              <button
                onClick={() => setOpen(true)}
                data-testid="all-in-one-quote-button"
                className="group flex items-center gap-2 rounded-full bg-[#D9A92E] px-7 py-3.5 text-sm font-semibold text-[#09090B] shadow-[0_0_25px_rgba(217,169,46,0.4)] transition-all duration-300 hover:bg-[#E8C05A] hover:shadow-[0_0_40px_rgba(217,169,46,0.6)]"
              >
                Request a Custom Quote
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <span className="mono text-xs uppercase tracking-[0.2em] text-[#6B7280]">
                Inquiry-only pricing
              </span>
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="relative">
            <div className="relative overflow-hidden rounded-2xl border border-white/10">
              <motion.img
                style={{ y }}
                src="/assets/all-in-one.png"
                alt="Golden power plug connecting with sparks — the All-In-One package"
                className="h-[420px] w-full scale-110 object-cover lg:h-[520px]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#131316] via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 rounded-xl border border-white/10 bg-[#09090B]/80 p-5 backdrop-blur-xl">
                <p className="mono text-[10px] uppercase tracking-[0.2em] text-[#E9C766]">
                  Turnkey launch
                </p>
                <p className="mt-1.5 text-sm font-medium text-white">
                  From first call to fully live — website, domain, hosting and leads in as little as 3 weeks.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
      <InquiryDialog open={open} onOpenChange={setOpen} service="all-in-one" />
    </section>
  );
};

export default AllInOne;
